import type { Route } from 'next';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

import { SearchQueryProps } from '@/components/app/cards/ContentCard/types';
import {
	ApiAsset,
	isCollection,
	isNarrator,
	isProgramOrGuide,
	isUrl,
	Pack,
	PackCardItem,
	PackItem,
} from '@/components/app/cards/PacksList/types';
import { usePacksContext } from '@/components/app/layout/PacksProvider';
import { useSimplifiedSignupModalContext } from '@/context/SimplifiedSignupModalContextProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useGuideDuration } from '@/hooks/app/useGuideDuration';
import { useGuidePlay } from '@/hooks/app/useGuidePlay';
import { useIsUnlockedForUser } from '@/hooks/app/useIsUnlockedForUser';
import { useLockedContentRouting } from '@/hooks/app/useLockedContentRouting';
import { calmLogger } from '@/utils/calmLogger';

// This hook renders on browse surfaces across the app (home, feeds, class pages, search,
// recommendations), so an unrenderable item can recur on every render of every affected
// card. Sampling keeps the signal (a report exists at all, and its type distribution) without
// flooding logs under normal traffic.
const UNRECOGNIZED_PACK_ITEM_SAMPLE_RATE = 0.01;

interface PackDisplayAttributes {
	title?: string;
	handlePackItemClick: (id?: string) => Promise<void>;
	handleUnlockedPackItemClick?: () => Promise<void>;
	key: string;
	duration?: string;
	is_new?: boolean;
	is_unlocked?: boolean;
	isLocked?: boolean;
	durationSeconds?: number;
	assetType?: string;
	detailImage?: ApiAsset;
	image?: ApiAsset;
	content_type?: string;
	description?: string;
	promotional_text?: string;
	full_subtitle?: string;
	narrator_name?: string;
	subtitle?: string;
	program_id?: string;
	guide_id?: string;
}

// Mirrors the render branches below, so the default branch can tell "an item type this client
// has no branch for" apart from a known type that merely lacks the fields its branch needs.
// Only the former is worth reporting.
function canRenderPackItem(packItem: PackCardItem): boolean {
	return (
		packItem.type === 'pack' ||
		isCollection(packItem) ||
		isUrl(packItem) ||
		isProgramOrGuide(packItem) ||
		isNarrator(packItem)
	);
}

export function determineKey(packItem: PackCardItem): string {
	// Both the b2b onclick_override collection and a plain search/feed collection carry
	// feed_id, so one branch keys both — distinct collections still get distinct keys.
	if (isCollection(packItem)) {
		return packItem.feed_id;
	}
	if (isNarrator(packItem)) {
		return packItem.narrator_id;
	}
	// A url item's title can be absent (the tout/banner-promo case) — fall back to the url so
	// this always returns a stable, non-empty key.
	if (isUrl(packItem)) {
		return packItem.title ?? packItem.url;
	}
	if (packItem.type === 'pack') {
		return packItem.title;
	}
	if (isProgramOrGuide(packItem)) {
		const { program_id, guide_id } = packItem;
		return guide_id ?? program_id;
	}

	return 'invalid-pack-item';
}

export function usePackItemAttributes({
	pack,
	packItem,
	searchQuery,
	onClickOverride,
	packFeedName,
}: {
	pack?: Pack;
	packItem: PackCardItem;
	searchQuery?: SearchQueryProps;
	onClickOverride?: (id?: string) => void;
	packFeedName?: string;
}): PackDisplayAttributes {
	const router = useRouter();
	const { logEvent } = useAnalytics();
	const onGuidePlay = useGuidePlay();
	const { setCurrentPack } = usePacksContext();
	const key = determineKey(packItem);
	const isUnlockedForUser = useIsUnlockedForUser();
	const { setIsModalOpen } = useSimplifiedSignupModalContext();
	const routeToUpsell = useLockedContentRouting();

	// Hooks can't be called from the type-specific branches below, which return early, so this
	// runs for every item and the gating lives in the argument: only an unlocked program/guide
	// has a length to show, and a locked row deliberately shows none.
	const durationText = useGuideDuration(
		isProgramOrGuide(packItem) && isUnlockedForUser(packItem, pack) ? packItem.duration : undefined,
	);

	// Fires in an effect, not during render: `Math.random` is impure, and this hook renders on
	// browse surfaces across the app where an unrenderable item can recur on every render of every
	// affected card, so sampling here is what keeps the signal (a report exists, and its type
	// distribution) from flooding logs under normal traffic.
	const isUnrenderable = !canRenderPackItem(packItem);
	const packItemType = packItem.type;
	useEffect(() => {
		// Keyed on the primitive `type`, not the `packItem` object, so a caller that rebuilds
		// its item each render can't turn a 1% sample into a per-render roll. The cost: within
		// one mounted instance, a second unrenderable item sharing the same `type` isn't
		// reported again.
		if (isUnrenderable && packItemType && Math.random() < UNRECOGNIZED_PACK_ITEM_SAMPLE_RATE) {
			calmLogger.error(
				'usePackItemAttributes: unrenderable pack item',
				{ packItemType },
				new Error('usePackItemAttributes received an item it has no render branch for'),
			);
		}
	}, [isUnrenderable, packItemType]);

	if (packItem.type === 'pack') {
		const { pack_class, title, subtitle, promotional_text, image, is_unlocked, is_new } =
			packItem as PackItem;
		return {
			key,
			image,
			handlePackItemClick: async () => {
				logEvent({
					eventName: 'Pack : Item : Clicked',
					eventProps: {
						title,
						id: title, // no actual id needed here
						pack_class,
						source: pack ? pack.pack_class : '',
					},
				});
				await router.push(`/app/class/${pack_class}` as Route);
			},
			title,
			subtitle,
			promotional_text,
			is_unlocked,
			is_new,
		};
	}

	// No duration on collection or url items
	if (isCollection(packItem) && packItem.onclick_override) {
		// TODO: In the future we will remove the necessary onClickOverride
		// assuming we support Packs
		// Right now it's a hacky solution to add it to prefab data on the client
		return {
			...packItem,
			title: packItem.title,
			subtitle: packItem.promotional_text,
			handlePackItemClick: packItem.onclick_override,
			key,
			is_new: packItem.is_new,
			is_unlocked: packItem.is_unlocked,
			image: packItem.image,
		};
	}
	// A plain collection result (e.g. search returning a "Calm for Kids"-style feed) —
	// no onclick_override, so it routes like any other browse destination: by its own
	// feed_id, through the generic feed page. is_unlocked/is_new/image are passed through
	// as the server sent them; there's no lock-gated upsell flow for collections the way
	// there is for programs/guides.
	if (isCollection(packItem)) {
		return {
			...packItem,
			title: packItem.title,
			subtitle: packItem.promotional_text,
			handlePackItemClick: async () => {
				logEvent({
					eventName: 'Pack : Item : Clicked',
					eventProps: {
						title: packItem.title,
						id: packItem.feed_id,
						source: pack ? pack.pack_class : '',
						// Collections are a search result like any other, so a click has to carry
						// the same query/position attribution the program and guide branches send.
						// Without it this is the one renderable result type whose click cannot be
						// traced back to the query and rank that produced it.
						query: searchQuery?.query ?? null,
						filter_category: searchQuery?.filter_category ?? null,
						filter_duration: searchQuery?.filter_duration ?? null,
						location: searchQuery?.location ?? null,
						pack_item_index: searchQuery?.pack_item_index ?? null,
						query_id: searchQuery?.query_id ?? null,
						search_index: searchQuery?.search_index ?? null,
						search_object_id: `feed_${packItem.feed_id}`,
					},
				});
				await router.push(`/app/${packItem.feed_id}` as Route);
			},
			key,
			is_new: packItem.is_new,
			is_unlocked: packItem.is_unlocked,
			image: packItem.image,
		};
	}
	// A narrator search result (e.g. "Tamara Levitt") — same generic feed page as a
	// collection, keyed by narrator_id instead of feed_id. is_unlocked/is_new/image are
	// passed through as the server sent them, same as collections.
	if (isNarrator(packItem)) {
		return {
			...packItem,
			title: packItem.title,
			handlePackItemClick: async () => {
				logEvent({
					eventName: 'Pack : Item : Clicked',
					eventProps: {
						title: packItem.title,
						id: packItem.narrator_id,
						source: pack ? pack.pack_class : '',
						query: searchQuery?.query ?? null,
						filter_category: searchQuery?.filter_category ?? null,
						filter_duration: searchQuery?.filter_duration ?? null,
						location: searchQuery?.location ?? null,
						pack_item_index: searchQuery?.pack_item_index ?? null,
						query_id: searchQuery?.query_id ?? null,
						search_index: searchQuery?.search_index ?? null,
						search_object_id: `narrator_${packItem.narrator_id}`,
					},
				});
				await router.push(`/app/narrator-${packItem.narrator_id}` as Route);
			},
			key,
			is_new: packItem.is_new,
			is_unlocked: packItem.is_unlocked,
			image: packItem.image,
		};
	}
	if (isUrl(packItem)) {
		// No title on the tout/banner-promo url item — its url is the only stable identifier
		// left to attribute the click to.
		const urlItemTitle = packItem.title ?? packItem.url;
		return {
			...packItem,
			title: packItem.title,
			subtitle: packItem.narrator_name,
			handlePackItemClick: async () => {
				logEvent({
					eventName: 'Pack : Item : Clicked',
					eventProps: {
						title: urlItemTitle,
						id: urlItemTitle, // no actual id needed here
					},
				});
				await router.push(packItem.url as Route);
			},
			key,
			is_new: packItem.is_new,
			is_unlocked: packItem.is_unlocked,
			image: packItem.image,
		};
	}
	if (isProgramOrGuide(packItem)) {
		// Standard packItem item now (display_type = 'carousel')
		// NOTE: 'carousel' is not the web carousel. That's `banner-carousel`. Web differs from mobile here
		const { program_id, guide_id } = packItem;
		const isLocked = !isUnlockedForUser(packItem, pack);

		const handleUnlockedPackItemClick = async () => {
			if (program_id === 'programString') {
				// not sure what this is for, but keeping the functionality for now
				await router.push('/app');
				return;
			}

			setCurrentPack(pack ?? null);
			setIsModalOpen(false);

			if (guide_id) {
				await onGuidePlay(guide_id);
			} else {
				await router.push(`/app/program/${program_id}` as Route);
			}
		};

		const onClick = async () => {
			if (onClickOverride) {
				onClickOverride(program_id ?? guide_id);
				return;
			}

			const contentId = guide_id || program_id || null;
			const contentType = guide_id ? 'guide' : program_id ? 'program' : null;

			if (isLocked) {
				logEvent({
					eventName: 'Web : Locked Content : Clicked',
					eventProps: {
						title: packItem.title,
						id: program_id || guide_id || null,
						guide_id: guide_id ?? null,
						query: searchQuery?.query ?? null,
						filter_category: searchQuery?.filter_category ?? null,
						filter_duration: searchQuery?.filter_duration ?? null,
						location: searchQuery?.location ?? null,
						pack_item_index: searchQuery?.pack_item_index ?? null,
						query_id: searchQuery?.query_id ?? null,
						search_index: searchQuery?.search_index ?? null,
						search_object_id: contentType ? `${contentType}_${contentId}` : null,
					},
				});

				await routeToUpsell(packItem);
				return;
			}

			logEvent({
				eventName: 'Pack : Item : Clicked',
				eventProps: {
					source: packFeedName || null,
					title: packItem.title,
					id: program_id || guide_id || null,
					guide_id: guide_id ?? null,
					query: searchQuery?.query ?? null,
					filter_category: searchQuery?.filter_category ?? null,
					filter_duration: searchQuery?.filter_duration ?? null,
					location: searchQuery?.location ?? null,
					pack_item_index: searchQuery?.pack_item_index ?? null,
					query_id: searchQuery?.query_id ?? null,
					search_index: searchQuery?.search_index ?? null,
					search_object_id: contentType ? `${contentType}_${contentId}` : null,
				},
			});

			await handleUnlockedPackItemClick();
		};

		return {
			...packItem,
			title: packItem.title,
			subtitle: packItem.subtitle,
			handlePackItemClick: onClick,
			handleUnlockedPackItemClick,
			key,
			duration: durationText,
			durationSeconds: packItem.duration,
			assetType: packItem.asset_type,
			detailImage: packItem.detail_image,
			isLocked,
			is_new: packItem.is_new,
			is_unlocked: packItem.is_unlocked,
			image: packItem.image,
			program_id,
			guide_id,
		};
	}

	// NOTE: default return may yield unexpected results
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const defaultPackItem = packItem as any;
	const onClick = async () => {};
	return {
		title: defaultPackItem?.title,
		subtitle: '',
		handlePackItemClick: onClick,
		key: 'invalid-pack-item',
		duration: '',
		is_new: defaultPackItem?.is_new,
		is_unlocked: defaultPackItem?.is_unlocked,
		// Absent when the item carries no asset. `image` is optional on the return type for
		// exactly this branch, and consumers guard rather than being handed a synthesized
		// asset that looks well-formed and still has no usable url.
		image: defaultPackItem?.image,
	};
}
